import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { FormInput, FormSelect } from '../../components/finance';
import DialogModal from '../../components/DialogModal';
import { Plus, Trash, ArrowLeft } from '@phosphor-icons/react';
import EnrollmentForm from '../../components/transport/EnrollmentForm';
import './transport.css';

function StudentEnrollmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [searchStudent, setSearchStudent] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [confirmDeleteEnrollmentId, setConfirmDeleteEnrollmentId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        setLoading(true);
        const data = await transportService.listEnrollments(pagination);
        console.log('Fetched enrollments:', data);
        setEnrollments(Array.isArray(data) ? data : data.enrollments || []);
      } catch (error) {
        toast.error('Failed to load enrollments: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, [pagination]);

  const handleCreate = async (formData) => {
    try {
      await transportService.createEnrollment(formData);
      toast.success('Student enrolled successfully');
      setShowModal(false);
      setSelectedEnrollment(null);
      const data = await transportService.listEnrollments(pagination);
      setEnrollments(Array.isArray(data) ? data : data.enrollments || []);
    } catch (error) {
      toast.error('Failed to enroll student: ' + error.message);
    }
  };

  const handleDelete = (id) => {
    setConfirmDeleteEnrollmentId(id);
  };

  const confirmDeleteEnrollment = async () => {
    if (!confirmDeleteEnrollmentId) return;
    setConfirmLoading(true);
    try {
      await transportService.deleteEnrollment(confirmDeleteEnrollmentId);
      toast.success('Enrollment removed successfully');
      const data = await transportService.listEnrollments(pagination);
      setEnrollments(Array.isArray(data) ? data : data.enrollments || []);
    } catch (error) {
      toast.error('Failed to remove enrollment: ' + error.message);
    } finally {
      setConfirmLoading(false);
      setConfirmDeleteEnrollmentId(null);
    }
  };

  const filteredEnrollments = enrollments.filter(e =>
    !searchStudent || 
    (e.student?.first_name || '').toLowerCase().includes(searchStudent.toLowerCase()) ||
    (e.student?.last_name || '').toLowerCase().includes(searchStudent.toLowerCase())
  );

  if (loading) {
    return <Layout><div className="loading">Loading enrollments...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page enrollment-management">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Student Enrollment</h1>
              <p>Manage student transport route enrollments</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedEnrollment(null); setShowModal(true); }}>
            <Plus size={18} /> Enroll Student
          </button>
        </div>

        <div className="filters-section">
          <FormInput
            label="Search Student"
            placeholder="Enter student name or ID..."
            value={searchStudent}
            onChange={(e) => setSearchStudent(e.target.value)}
          />
        </div>

        <div className="table-section">
          {filteredEnrollments.length === 0 ? (
            <div className="empty-state">
              <p>No enrollments found. Enroll your first student.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Student ID</th>
                  <th>Route</th>
                  <th>Pickup Point</th>
                  <th>Dropoff Point</th>
                  <th>Enrollment Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnrollments.map(enrollment => (
                  <tr key={enrollment.id}>
                    <td><strong>{enrollment.student?.first_name} {enrollment.student?.last_name}</strong></td>
                    <td>{enrollment.student?.student_id}</td>
                    <td>{enrollment.route?.route_name}</td>
                    <td>{enrollment.pickup_point || '-'}</td>
                    <td>{enrollment.dropoff_point || '-'}</td>
                    <td>{enrollment.enrollment_date}</td>
                    <td><span className={`badge ${enrollment.is_active ? 'badge-success' : 'badge-danger'}`}>{enrollment.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="actions">
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(enrollment.id)} title="Remove">
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button disabled={pagination.skip === 0} onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}>
            Previous
          </button>
          <span>Page {Math.floor(pagination.skip / pagination.limit) + 1}</span>
          <button disabled={filteredEnrollments.length < pagination.limit} onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}>
            Next
          </button>
        </div>

        {showModal && (
          <EnrollmentForm
            enrollment={selectedEnrollment}
            onSave={handleCreate}
            onClose={() => { setShowModal(false); setSelectedEnrollment(null); }}
          />
        )}

        <DialogModal
          isOpen={Boolean(confirmDeleteEnrollmentId)}
          onClose={() => setConfirmDeleteEnrollmentId(null)}
          title="Remove Enrollment"
          message="Are you sure you want to remove this enrollment? This action cannot be undone."
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Removing...' : 'Remove', onClick: confirmDeleteEnrollment }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />
      </div>
    </Layout>
  );
}

export default StudentEnrollmentPage;
